/**
 * Badge discreto de status da API no canto inferior esquerdo (PR #69).
 *
 * Faz GET /api/v1/health a cada 30s e mostra:
 * - 🟢 "online" se 200
 * - 🔴 "offline" se falhar
 * - Tooltip com latência ms
 *
 * Não bloqueia render — falha silenciosa. Usado pra dar sinal mínimo do
 * health do prod em qualquer página. Off em dev por default (já tem
 * outros sinais), opcional ligar via localStorage.acordei.showHealth=1.
 */
import { useEffect, useState } from "react";

const BASE_URL: string =
  (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ??
  "/api/v1";

type EstadoSaude = "checando" | "online" | "offline";

export default function HealthBadge() {
  const [estado, setEstado] = useState<EstadoSaude>("checando");
  const [latencia, setLatencia] = useState<number | null>(null);
  const [visivel, setVisivel] = useState(false);

  // Visibilidade: em dev/staging só mostra se localStorage flag, em prod sempre
  useEffect(() => {
    try {
      const flag = window.localStorage.getItem("acordei.showHealth");
      if (flag === "1") {
        setVisivel(true);
        return;
      }
      if (flag === "0") {
        setVisivel(false);
        return;
      }
    } catch {
      /* localStorage indisponível */
    }
    // Default: mostra em hostname de produção (não localhost / dev)
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    setVisivel(!/(localhost|127\.0\.0\.1|\.local$)/.test(host));
  }, []);

  // Polling 30s
  useEffect(() => {
    if (!visivel) return;
    let cancelado = false;
    let timer: number | undefined;

    async function checar() {
      const t0 = performance.now();
      try {
        const ctrl = new AbortController();
        const timeoutId = window.setTimeout(() => ctrl.abort(), 8000);
        const r = await fetch(`${BASE_URL}/health`, { signal: ctrl.signal });
        window.clearTimeout(timeoutId);
        const ms = Math.round(performance.now() - t0);
        if (!cancelado) {
          setLatencia(ms);
          setEstado(r.ok ? "online" : "offline");
        }
      } catch {
        if (!cancelado) {
          setEstado("offline");
          setLatencia(null);
        }
      }
    }

    void checar();
    timer = window.setInterval(checar, 30_000);
    return () => {
      cancelado = true;
      if (timer) window.clearInterval(timer);
    };
  }, [visivel]);

  if (!visivel) return null;

  const cor =
    estado === "online" ? "#34d399" : estado === "offline" ? "#f87171" : "#94a3b8";
  const label =
    estado === "online" ? "API online" : estado === "offline" ? "API offline" : "verificando...";

  return (
    <div
      role="status"
      aria-live="polite"
      title={
        latencia != null
          ? `Status: ${label} (${latencia}ms) · clique pra esconder`
          : `Status: ${label}`
      }
      onClick={() => {
        try {
          window.localStorage.setItem("acordei.showHealth", "0");
        } catch {
          /* ignore */
        }
        setVisivel(false);
      }}
      style={{
        position: "fixed",
        bottom: "8px",
        left: "8px",
        zIndex: 40,
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "10px",
        fontFamily: "monospace",
        background: "rgba(15, 23, 42, 0.7)",
        backdropFilter: "blur(8px)",
        border: `1px solid ${cor}33`,
        color: cor,
        cursor: "pointer",
        opacity: 0.6,
        transition: "opacity 0.2s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
    >
      <span
        style={{
          display: "inline-block",
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: cor,
          boxShadow: estado === "online" ? `0 0 6px ${cor}` : "none",
        }}
        aria-hidden="true"
      />
      {label}
      {latencia != null && estado === "online" && (
        <span style={{ opacity: 0.6 }}>· {latencia}ms</span>
      )}
    </div>
  );
}
